/**
 * `JobPickerSheet` — shared job-selection sheet used by both the top-bar
 * header pill (`Layout.tsx > JobSelector`) and the Active Job card on the
 * Capture home widget. Ensures a single dark + orange visual language for
 * job selection across the app:
 *   • Sheet drops from top with dark #1F1F1F surface
 *   • Selected row: soft orange fill + orange border + orange Check icon
 *   • Unselected rows: `bi-surface` (quiet dark grey), hover brightens
 *   • Search input reuses `.bi-input` for consistency
 *
 * The trigger is passed via `children` (render-prop-esque) so consumers can
 * style their own pill / button — behaviour is centralised here.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Check } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useApp } from "@/contexts/AppContext";

export default function JobPickerSheet({ children, testIdPrefix = "job" }) {
  const { jobs, activeJobId, setActiveJobId } = useApp();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = jobs.filter((j) =>
    j.name.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQ("");
      }}
    >
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        side="top"
        className="bg-[#1F1F1F] border-white/10 text-white"
      >
        <SheetHeader>
          <SheetTitle className="font-heading">Active Job</SheetTitle>
          <SheetDescription className="text-white/50">
            Choose which job you&apos;re logging expenses to.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none z-10" />
          <input
            data-testid={`${testIdPrefix}-search-input`}
            placeholder="Search jobs…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-11 bi-input pl-10 pr-3 text-sm"
          />
        </div>
        <div className="mt-3 max-h-[50vh] overflow-y-auto space-y-2">
          {filtered.map((j) => {
            const isActive = activeJobId === j.id;
            return (
              <button
                key={j.id}
                data-testid={`${testIdPrefix}-option-${j.id}`}
                onClick={() => {
                  setActiveJobId(j.id);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-xl transition flex items-center justify-between gap-3 ${
                  isActive
                    ? "bg-[rgba(255,149,0,0.15)] border border-[#FF9500]/40"
                    : "bi-surface border border-transparent hover:brightness-110"
                }`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{j.name}</div>
                  {j.client && (
                    <div className="text-xs text-white/50 truncate">
                      {j.client}
                    </div>
                  )}
                </div>
                {isActive && (
                  <Check
                    className="w-4 h-4 text-[#FF9500] shrink-0"
                    data-testid={`${testIdPrefix}-option-check-${j.id}`}
                  />
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-white/50 text-sm px-2 py-6 text-center">
              No jobs. Create one in{" "}
              <Link to="/jobs" className="text-[#FF9500]">
                Jobs
              </Link>
              .
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
